import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Clock } from "lucide-react";
import { searchRoutes, getRoutesByCategory, RouteEntry } from "../../config/routes.registry";
import { useEffectiveRole } from "../../hooks/useEffectiveRole";
import useAuthorizedModules from "../../hooks/useAuthorizedModules";

const RECENT_KEY = "clu_mobile_recent_routes";
const MAX_RECENTS = 6;

// Stable identity wrapper so isModuleAuthorized never causes effect re-runs
function useStableModuleCheck() {
  const { isModuleAuthorized } = useAuthorizedModules();
  const ref = useRef(isModuleAuthorized);
  ref.current = isModuleAuthorized;
  return useCallback((code: string | undefined) => ref.current(code), []);
}

function getRecents(): RouteEntry[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveRecent(route: RouteEntry) {
  try {
    const recents = getRecents().filter((r) => r.mobilePath !== route.mobilePath);
    recents.unshift(route);
    localStorage.setItem(RECENT_KEY, JSON.stringify(recents.slice(0, MAX_RECENTS)));
  } catch {
    // localStorage not available, ignore
  }
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<RouteEntry[]>([]);
  const [recents, setRecents] = useState<RouteEntry[]>([]);
  const [byCategory, setByCategory] = useState<Record<string, RouteEntry[]>>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { effectiveRole } = useEffectiveRole();
  const checkModule = useStableModuleCheck();

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setRecents(getRecents());
      setByCategory(getRoutesByCategory(effectiveRole || "user", checkModule));
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [isOpen, effectiveRole, checkModule]);

  useEffect(() => {
    if (query.trim()) {
      setResults(searchRoutes(query, effectiveRole || "user", checkModule));
    } else {
      setResults([]);
    }
  }, [query, effectiveRole, checkModule]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  const handleSelect = useCallback(
    (route: RouteEntry) => {
      saveRecent(route);
      navigate(route.mobilePath);
      onClose();
    },
    [navigate, onClose]
  );

  if (!isOpen) return null;

  const showResults = query.trim().length > 0;
  const showRecents = !showResults && recents.length > 0;
  const showBrowse = !showResults;

  return (
    <div className="command-palette-overlay" onClick={onClose}>
      <div
        className="command-palette-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="command-palette-header">
          <div className="command-palette-search-row">
            <Search size={18} className="command-palette-search-icon" />
            <input
              ref={inputRef}
              type="text"
              className="command-palette-input"
              placeholder="Search modules, settings, tools…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button
                type="button"
                className="command-palette-clear"
                onClick={() => setQuery("")}
              >
                <X size={16} />
              </button>
            )}
          </div>
          <button type="button" className="command-palette-cancel" onClick={onClose}>
            Cancel
          </button>
        </div>

        <div className="command-palette-body">
          {showResults && (
            <section>
              {results.length === 0 ? (
                <p className="command-palette-empty">No results for "{query}"</p>
              ) : (
                results.map((route) => (
                  <RouteItem key={route.mobilePath} route={route} onSelect={handleSelect} />
                ))
              )}
            </section>
          )}

          {showRecents && (
            <section>
              <p className="command-palette-section-label">
                <Clock size={12} />
                Recent
              </p>
              {recents.map((route) => (
                <RouteItem key={route.mobilePath} route={route} onSelect={handleSelect} />
              ))}
            </section>
          )}

          {showBrowse && (
            <section>
              {Object.entries(byCategory).map(([category, routes]) => (
                <div key={category}>
                  <p className="command-palette-section-label">{category}</p>
                  {routes.map((route) => (
                    <RouteItem key={route.mobilePath} route={route} onSelect={handleSelect} />
                  ))}
                </div>
              ))}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

interface RouteItemProps {
  route: RouteEntry;
  onSelect: (route: RouteEntry) => void;
}

const RouteItem: React.FC<RouteItemProps> = ({ route, onSelect }) => (
  <button
    type="button"
    className="command-palette-item"
    onClick={() => onSelect(route)}
  >
    <span className="command-palette-item-icon">{route.icon}</span>
    <span className="command-palette-item-info">
      <span className="command-palette-item-label">{route.label}</span>
      <span className="command-palette-item-category">{route.category}</span>
    </span>
  </button>
);

export default CommandPalette;
